import { ClientNote, clientNotesService } from 'src/services/clientNotesService';
import { clientTimelineService, TimelineEvent } from 'src/services/clientTimelineService';
import { ClientVaultDocument, clientVaultService } from 'src/services/clientVaultService';
import { type ContractReviewDatabaseResult, contractService } from 'src/services/contractService';
import { dataService, type LegalResearchDatabaseResult } from 'src/services/dataService';
import { groupResearchesByVersion, type LegalResearch, mapDatabaseRowToResearch } from 'src/services/researchMapper';

export const PREFETCH_TOP_N = 10;

export const vaultDocumentsCache = new Map<string, ClientVaultDocument[]>();
export const clientNotesCache = new Map<string, ClientNote[]>();
export const clientResearchCache = new Map<string, LegalResearch[]>();
export const clientVersionMapCache = new Map<string, Map<string, LegalResearch[]>>();
export const clientContractReviewsByDocCache = new Map<string, Record<string, ContractReviewDatabaseResult>>();
export const clientTimelineCache = new Map<string, TimelineEvent[]>();

const inflightPrefetch = new Map<string, Promise<void>>();
let prefetchedSession: string | null = null;

export const setVaultDocumentsCache = (clientId: string, documents: ClientVaultDocument[]): void => {
  vaultDocumentsCache.set(clientId, documents);
};

export const setClientNotesCache = (clientId: string, notes: ClientNote[]): void => {
  clientNotesCache.set(clientId, notes);
};

export const setClientResearchCache = (
  clientId: string,
  researches: LegalResearch[],
  versionMap: Map<string, LegalResearch[]>
): void => {
  clientResearchCache.set(clientId, researches);
  clientVersionMapCache.set(clientId, versionMap);
};

export const setClientContractReviewsCache = (
  clientId: string,
  byDocId: Record<string, ContractReviewDatabaseResult>
): void => {
  clientContractReviewsByDocCache.set(clientId, byDocId);
};

export const setClientTimelineCache = (clientId: string, events: TimelineEvent[]): void => {
  clientTimelineCache.set(clientId, events);
};

export const clearClientCaches = (clientId?: string): void => {
  if (clientId) {
    vaultDocumentsCache.delete(clientId);
    clientNotesCache.delete(clientId);
    clientResearchCache.delete(clientId);
    clientVersionMapCache.delete(clientId);
    clientContractReviewsByDocCache.delete(clientId);
    clientTimelineCache.delete(clientId);
    return;
  }
  vaultDocumentsCache.clear();
  clientNotesCache.clear();
  clientResearchCache.clear();
  clientVersionMapCache.clear();
  clientContractReviewsByDocCache.clear();
  clientTimelineCache.clear();
  inflightPrefetch.clear();
  prefetchedSession = null;
};

const fetchResearchAndCache = async (clientId: string): Promise<void> => {
  const data = await dataService.legalResearch.getClientResearchResults(clientId);
  const mapped = data
    .map((row: LegalResearchDatabaseResult) => mapDatabaseRowToResearch(row))
    .filter((item: LegalResearch | null): item is LegalResearch => !!item);
  const { latestResearches, versionMap } = groupResearchesByVersion(mapped);
  clientResearchCache.set(clientId, latestResearches);
  clientVersionMapCache.set(clientId, versionMap);
};

const fetchContractReviewsAndCache = async (clientId: string): Promise<void> => {
  const reviews = await contractService.getClientContractReviews(clientId);
  const map: Record<string, ContractReviewDatabaseResult> = {};
  for (const r of reviews) {
    const req = r.request as { document_id?: string; contract_id?: string };
    const docId = req.document_id ?? req.contract_id;
    if (docId && !(docId in map)) map[docId] = r;
  }
  clientContractReviewsByDocCache.set(clientId, map);
};

const prefetchOne = (clientId: string, clientName?: string): Promise<void> => {
  const existing = inflightPrefetch.get(clientId);
  if (existing) return existing;

  const promise = Promise.allSettled([
    clientVaultService.listByClient(clientId).then((docs) => {
      vaultDocumentsCache.set(clientId, docs);
    }),
    clientNotesService.listByClient(clientId).then((notes) => {
      clientNotesCache.set(clientId, notes);
    }),
    fetchResearchAndCache(clientId),
    fetchContractReviewsAndCache(clientId),
    clientTimelineService.getClientTimeline(clientId, clientName).then((events) => {
      clientTimelineCache.set(clientId, events);
    }),
  ]).then(() => {
    inflightPrefetch.delete(clientId);
  });

  inflightPrefetch.set(clientId, promise);
  return promise;
};

export interface PrefetchTarget {
  id: string;
  name?: string;
}

export const prefetchTopClients = async (
  clients: PrefetchTarget[],
  limit = PREFETCH_TOP_N,
  sessionKey?: string
): Promise<void> => {
  if (sessionKey && prefetchedSession === sessionKey) return;
  prefetchedSession = sessionKey ?? null;
  const targets = clients
    .slice(0, limit)
    .filter(
      (c) =>
        !vaultDocumentsCache.has(c.id) ||
        !clientNotesCache.has(c.id) ||
        !clientResearchCache.has(c.id) ||
        !clientContractReviewsByDocCache.has(c.id) ||
        !clientTimelineCache.has(c.id)
    );
  await Promise.allSettled(targets.map((c) => prefetchOne(c.id, c.name)));
};
